import React, { useState, useEffect } from "react";
import { User, Organization } from "@pollyslack/hub/src/types";
import NoDataMessage from "../../../common/NoDataMessage";
import { ExpanderToggle } from "../../../components/Expander";
import { Code } from "../../../components/Code";
import * as SlackMessagesAPI from "../../../services/api/SlackMessages";
import * as UserAPI from "../../../services/api/User";
import styled from "styled-components";
import Loader from "../../../common/Loader";
import { UserLink } from "../../../components/DocumentLink";

// Styled Components - Consolidated for better readability
const StyledTable = styled.table.attrs({
  className: "table table-dark table-sm table-hover overflow-auto",
})``;

const TableCell = styled.td`
  vertical-align: middle !important;
`;

const CaretCell = styled(TableCell)`
  width: 40px;
`;

const JsonContent = styled.div`
  max-height: 400px;
  overflow-y: auto;
  width: 100%;

  pre,
  code,
  .react-syntax-highlighter-line {
    white-space: pre-wrap !important;
    word-break: break-word !important;
    overflow-x: hidden !important;
  }
`;

const ExpandedContent = styled.td.attrs({
  colSpan: 8,
  className: "p-3",
})`
  max-width: 0; /* Forces cell to respect width constraints */
`;

// Types
interface MarketingDmRow {
  _id: string;
  messageId: string;
  sendDate: string;
  userId?: string;
  userEmail?: string;
  userTitle?: string;
  type: string;
  messageName: string;
}

// Define message structure
interface CampaignMessage {
  messageId?: string;
  messageName: string;
  sendDate: string | Date;
  [key: string]: any; // For other properties
}

// Define campaign structure with messages
interface Campaign {
  type: string;
  sendDate: string | Date;
  messages?: CampaignMessage[];
  _userId?: string;
  _userEmail?: string;
  _userTitle?: string;
  [key: string]: any; // For other properties
}

type MarketingDmsTableProps = {
  user?: User;
  organization?: Organization;
};

// Loading component to keep the JSX cleaner
const LoadingIndicator = () => (
  <div className="text-center">
    <div className="spinner-border text-primary" role="status">
      <span className="sr-only">Loading...</span>
    </div>
    <div className="mt-2">Fetching message data...</div>
  </div>
);

// Table Row Component
const MarketingDmTableRow: React.FC<{ row: MarketingDmRow }> = ({ row }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messageData, setMessageData] = useState<any>(null);
  const hasMessageId = row.messageId !== "—";

  // Only fetch data when expanded and not already loaded
  useEffect(() => {
    if (expanded && !messageData && hasMessageId) {
      setLoading(true);
      SlackMessagesAPI.info(row.messageId)
        .then((data) => {
          setMessageData(data);
          setLoading(false);
        })
        .catch((error) => {
          alert("Error fetching message: " + error);
          setLoading(false);
        });
    }
  }, [expanded, messageData, row.messageId, hasMessageId]);

  return (
    <>
      <tr>
        <CaretCell>
          {hasMessageId && (
            <ExpanderToggle
              expanded={expanded}
              toggle={() => setExpanded(!expanded)}
              label=""
            />
          )}
        </CaretCell>
        <TableCell>{row.messageId}</TableCell>
        <TableCell>
          {new Date(row.sendDate).toISOString().split("T")[0]}
        </TableCell>
        <TableCell>
          {row.userId && row.userId !== "—" ? (
            <UserLink docId={row.userId}>{row.userId}</UserLink>
          ) : (
            row.userId
          )}
        </TableCell>
        <TableCell>{row.userEmail}</TableCell>
        <TableCell>{row.userTitle}</TableCell>
        <TableCell>{row.type}</TableCell>
        <TableCell>{row.messageName}</TableCell>
      </tr>

      {/* Expanded content row */}
      {hasMessageId && expanded && (
        <tr>
          <ExpandedContent>
            {loading ? (
              <LoadingIndicator />
            ) : (
              messageData && (
                <JsonContent>
                  <Code object={messageData} />
                </JsonContent>
              )
            )}
          </ExpandedContent>
        </tr>
      )}
    </>
  );
};

// Main Component
export const MarketingDmsTable: React.FC<MarketingDmsTableProps> = ({
  user,
  organization,
}) => {
  const [orgUsers, setOrgUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch users for the organization
  useEffect(() => {
    if (organization && !user) {
      let cancelled = false;
      async function loadUsers() {
        setIsLoading(true);
        try {
          const users = await UserAPI.list({
            search: organization._id,
            onlyMarketingDms: true,
          });
          if (!cancelled) {
            setOrgUsers(users);
          }
        } catch (error) {
          alert("Error loading users for organization: " + error);
        } finally {
          if (!cancelled) {
            setIsLoading(false);
          }
        }
      }
      loadUsers();
      return () => {
        cancelled = true;
      };
    }
    // Make sure to return something for all code paths
    return undefined;
  }, [organization, user]);

  // Extract data from user object or organization users
  let marketingDms: Campaign[] = [];
  let userId: string | undefined;
  let userEmail: string | undefined;
  let userTitle: string | undefined;

  if (user) {
    // Case 1: User is provided directly
    marketingDms = user.profile?.marketingMessages || [];
    userId = user._id;
    userEmail = user.profile?.email;
    userTitle = user.profile?.title;
  } else if (organization && orgUsers.length > 0) {
    // Case 2: Organization is provided, and users are loaded
    // Combine marketing messages from all users, preserving user info for each message
    marketingDms = orgUsers.reduce((allMessages, orgUser) => {
      if (orgUser.profile?.marketingMessages?.length) {
        // Enhance each message with user information
        const enhancedMessages = orgUser.profile.marketingMessages.map(
          (campaign) => ({
            ...campaign,
            // Add user information to the campaign
            _userId: orgUser._id,
            _userEmail: orgUser.profile?.email,
            _userTitle: orgUser.profile?.title,
          }),
        );
        return [...allMessages, ...enhancedMessages];
      }
      return allMessages;
    }, [] as Campaign[]);
  }

  // Transform the campaign data into rows - Move this hook before any conditional returns
  const rows = React.useMemo(() => {
    if (!marketingDms.length) return [];

    const allRows = marketingDms.flatMap((campaign) => {
      // If no messages, create a placeholder row
      if (!campaign.messages?.length) {
        return [
          {
            _id: `${campaign.type}-${campaign.sendDate}`,
            messageId: "—",
            sendDate: new Date(campaign.sendDate).toISOString(),
            userId: campaign._userId || userId || "—",
            userEmail: campaign._userEmail || userEmail || "—",
            userTitle: campaign._userTitle || userTitle || "—",
            type: campaign.type,
            messageName: "—",
          },
        ];
      }

      // Create a row for each message
      return campaign.messages.map((message: CampaignMessage) => ({
        _id: message.messageId || `${message.messageName}-${message.sendDate}`,
        messageId: message.messageId || "—",
        sendDate: new Date(message.sendDate).toISOString(),
        userId: campaign._userId || userId || "—",
        userEmail: campaign._userEmail || userEmail || "—",
        userTitle: campaign._userTitle || userTitle || "—",
        type: campaign.type,
        messageName: message.messageName,
      }));
    });
    return allRows.sort(
      (a, b) => new Date(b.sendDate).getTime() - new Date(a.sendDate).getTime(),
    );
  }, [marketingDms, userId, userEmail, userTitle]);

  // Now we can have conditional returns AFTER all hooks are called
  if (!user && !organization) {
    console.warn(
      "Neither user nor organization prop provided to MarketingDmsTable",
    );
    return <NoDataMessage componentName="marketing messages" />;
  }

  // Show loading state when fetching users for an organization
  if (organization && !user && isLoading) {
    return <Loader />;
  }

  if (!rows.length) {
    return <NoDataMessage componentName="marketing messages" />;
  }

  return (
    <div>
      <StyledTable>
        <thead>
          <tr>
            <th />
            <th>Message ID</th>
            <th>Send Date</th>
            <th>User ID</th>
            <th>User Email</th>
            <th>User Title</th>
            <th>Type</th>
            <th>Message Name</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <MarketingDmTableRow key={row._id} row={row} />
          ))}
        </tbody>
      </StyledTable>
    </div>
  );
};
